import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { ShieldOff, HelpCircle, Send, FileSearch, UserCheck, Clock, Mail } from "lucide-react"
import { SEO } from "@/components/SEO"

const DoNotShare = () => {
    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            <SEO
                title="Do Not Share My Personal Information | RSA Tech Group"
                description="Exercise your right to opt out of the sharing of your personal information. Learn how to submit a Do Not Share request."
            />
            <Navigation />

            {/* Hero Section */}
            <div className="relative pt-48 pb-20 overflow-hidden bg-gradient-to-b from-[#0F172A] to-[#09090b]">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-[120px] opacity-40 mix-blend-screen" />
                    <div className="absolute bottom-0 left-[-100px] w-[500px] h-[500px] bg-indigo-900/10 rounded-full blur-[100px] opacity-30" />
                </div>
                <div className="section-container relative z-10 text-center max-w-4xl mx-auto">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-white">
                        Do Not Share My Personal Information
                    </h1>
                    <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto">
                        We respect your privacy rights. Learn how to opt out of the sharing of your personal information.
                    </p>
                    <div className="mt-8 text-sm text-gray-400 font-medium tracking-wide uppercase">
                        Last Updated: January 2026
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <section className="py-24 bg-slate-50 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('/digital-transform-bg-rectangles.png')] bg-cover bg-center opacity-[0.03]" />

                <div className="section-container relative z-10 max-w-4xl mx-auto">
                    <div className="bg-white/80 backdrop-blur-md border border-slate-200 rounded-2xl p-8 md:p-12 shadow-sm">

                        {/* 1. Your Privacy Rights */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                                    <ShieldOff className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">1. Your Privacy Rights</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    Under the California Consumer Privacy Act (CCPA), the California Privacy Rights Act (CPRA), and similar state privacy laws, you have the right to opt out of the "sale" or "sharing" of your personal information to third parties.
                                </p>
                                <p>
                                    RSA Tech Group respects your right to control your personal data. We do not sell personal information. However, certain uses of cookies, analytics, or advertising technologies may constitute "sharing" under applicable law. This page allows you to exercise your opt-out rights.
                                </p>
                            </div>
                        </div>

                        {/* 2. What "Do Not Share" Means */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                                    <HelpCircle className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">2. What "Do Not Share" Means</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    "Sharing" under privacy law refers to the disclosure of personal information to a third party for cross-context behavioral advertising purposes. When you submit a "Do Not Share" request:
                                </p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>We will stop disclosing your personal information to third parties for targeted advertising purposes.</li>
                                    <li>We will disable third-party tracking cookies and similar technologies on your browser where technically feasible.</li>
                                    <li>We will continue to use your information only as necessary to provide our services, comply with legal obligations, and for other exempted purposes under applicable law.</li>
                                </ul>
                                <p>
                                    Note: Opting out does not affect data sharing that is necessary for providing our core services or complying with legal requirements.
                                </p>
                            </div>
                        </div>

                        {/* 3. How to Submit a Request */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                                    <Send className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">3. How to Submit a Request</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>You can submit a "Do Not Share My Personal Information" request through any of the following methods:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li><strong>Email:</strong> Send a request to <a href="mailto:privacy@rsatechgroup.com" className="text-blue-600 hover:underline">privacy@rsatechgroup.com</a> with the subject line "Do Not Share Request."</li>
                                    <li><strong>Cookie Preferences:</strong> You can manage your cookie preferences directly through our cookie consent banner on any page of our website.</li>
                                    <li><strong>Global Privacy Control (GPC):</strong> We honor GPC signals sent by your browser. If you have GPC enabled, we will automatically treat your visit as an opt-out of sharing.</li>
                                </ul>

                                {/* CTA Button */}
                                <div className="mt-8 pt-4">
                                    <a
                                        href="mailto:privacy@rsatechgroup.com?subject=Do%20Not%20Share%20Request"
                                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                                    >
                                        <Mail className="w-4 h-4" />
                                        Submit Opt-Out Request
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* 4. What Information Is Covered */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600">
                                    <FileSearch className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">4. What Information Is Covered</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>The categories of personal information that may be subject to sharing include:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li><strong>Identifiers:</strong> Such as name, email address, and IP address.</li>
                                    <li><strong>Internet Activity:</strong> Including browsing history, search history, and information regarding your interaction with our website.</li>
                                    <li><strong>Geolocation Data:</strong> Approximate location derived from your IP address.</li>
                                    <li><strong>Inferences:</strong> Profiles reflecting your preferences, characteristics, or behavior drawn from the above categories.</li>
                                </ul>
                                <p>
                                    For a complete list of categories of personal information we collect and our purposes for processing, please refer to our <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a>.
                                </p>
                            </div>
                        </div>

                        {/* 5. Verification Process */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600">
                                    <UserCheck className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">5. Verification Process</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    To protect your privacy and prevent fraudulent requests, we may need to verify your identity before processing your request. The verification process may include:
                                </p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Confirming your email address or other contact information on file.</li>
                                    <li>Matching identifying information provided in your request against information we already maintain.</li>
                                    <li>Requesting additional documentation in limited circumstances where we cannot otherwise verify your identity.</li>
                                </ul>
                                <p>
                                    We will not require you to create an account with us solely for the purpose of verifying your request.
                                </p>
                            </div>
                        </div>

                        {/* 6. Response Timeline */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600">
                                    <Clock className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">6. Response Timeline</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    We will acknowledge your request within 10 business days and complete your request within 45 calendar days of receiving it, as required by applicable law. If we need additional time (up to 45 more days), we will notify you in writing with an explanation of the reason for the delay.
                                </p>
                                <p>
                                    You will not be discriminated against, charged different prices, or provided a different level of service for exercising your privacy rights.
                                </p>
                            </div>
                        </div>

                        {/* 7. Contact Us */}
                        <div className="mt-16 pt-12 border-t border-slate-100">
                            <h2 className="text-2xl font-bold text-slate-900 mb-6">7. Contact Us</h2>
                            <p className="text-slate-600 mb-6">
                                If you have questions about your privacy rights or need assistance submitting a request, please contact us:
                            </p>
                            <div className="flex flex-col gap-2 pl-3 md:pl-0">
                                <div className="font-bold text-slate-900">RSA Tech Group — Privacy Team</div>
                                <div className="flex items-center gap-3 text-slate-800 font-medium">
                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                        <Mail className="w-4 h-4" />
                                    </div>
                                    <a href="mailto:privacy@rsatechgroup.com" className="hover:text-blue-600 transition-colors">privacy@rsatechgroup.com</a>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default DoNotShare
